import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Loader2, Mail, Phone, Building2, Calendar, Search, CheckCircle2, Circle, Trash2, MessageSquare, Inbox
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHandler';

const ContattiPage = () => {
  const { API } = useAuth();
  const [contatti, setContatti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchContatti(); }, []);

  const fetchContatti = async () => {
    try {
      const res = await axios.get(`${API}/contatti`);
      setContatti(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore caricamento contatti'));
    } finally { setLoading(false); }
  };

  const handleToggleLetto = async (id, letto) => {
    try {
      await axios.put(`${API}/contatti/${id}`, { letto: !letto });
      setContatti(prev => prev.map(c => c.id === id ? { ...c, letto: !letto } : c));
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore aggiornamento'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo contatto?')) return;
    try {
      await axios.delete(`${API}/contatti/${id}`);
      setContatti(prev => prev.filter(c => c.id !== id));
      toast.success('Contatto eliminato');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore eliminazione'));
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const filtered = contatti.filter(c =>
    !search || [c.nome, c.email, c.organizzazione, c.messaggio].some(f => f?.toLowerCase().includes(search.toLowerCase()))
  );

  const nonLetti = contatti.filter(c => !c.letto).length;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="contatti-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Contatti Ricevuti</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {contatti.length} contatti totali {nonLetti > 0 && <Badge variant="destructive" className="ml-2 text-[10px]">{nonLetti} nuovi</Badge>}
          </p>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca per nome, email, organizzazione..." className="pl-9" data-testid="contatti-search" />
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="p-8 sm:p-12 text-center">
            <Inbox className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">{contatti.length === 0 ? 'Nessun contatto ricevuto' : 'Nessun risultato'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(c => (
            <Card key={c.id} className={!c.letto ? 'border-primary/40 bg-primary/[0.02]' : 'opacity-80'} data-testid={`contatto-${c.id}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <button onClick={() => handleToggleLetto(c.id, c.letto)} className="mt-1 flex-shrink-0" title={c.letto ? 'Segna come non letto' : 'Segna come letto'}>
                    {c.letto
                      ? <CheckCircle2 className="w-5 h-5 text-green-500" />
                      : <Circle className="w-5 h-5 text-primary" />
                    }
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-sm sm:text-base font-semibold">{c.nome}</h3>
                      {!c.letto && <Badge className="text-[10px] bg-primary">Nuovo</Badge>}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-2">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>
                      {c.telefono && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.telefono}</span>}
                      {c.organizzazione && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{c.organizzazione}</span>}
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(c.created_at)}</span>
                    </div>
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/40">
                      <MessageSquare className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-foreground whitespace-pre-line">{c.messaggio}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600 flex-shrink-0"
                    onClick={() => handleDelete(c.id)} data-testid={`delete-contatto-${c.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContattiPage;
